import { writable, derived } from "svelte/store";
import { appendAssistantMessage, getMessagesForChat } from "../stores/chat-store.js";

export interface ChatContext {
  chatId: string;
  contextType: "projects" | "documents";
  isGenerating: boolean;
  hasMessages: boolean;
  requestId?: string;
}

// Type for the IDs array to make it explicit
export type ChatIds = string[];

export interface ChatState {
  currentChatId: string | null;
  currentContextType: "projects" | "documents" | null;
  isGenerating: boolean;
  showThinkingState: boolean;
  showEmptyState: boolean;
}

class UnifiedChatManager {
  private chatContexts = new Map<string, ChatContext>();
  private currentChatId: string | null = null;
  private currentContextType: "projects" | "documents" | null = null;

  // Store for reactive updates
  private chatContextsStore = writable<Map<string, ChatContext>>(this.chatContexts);
  private currentChatStore = writable<string | null>(null);
  private currentContextTypeStore = writable<"projects" | "documents" | null>(null);

  // Derived state that combines all information
  public state = derived([this.chatContextsStore, this.currentChatStore, this.currentContextTypeStore], ([$chatContexts, $currentChatId, $currentContextType]) => {
    // Get the current context's state
    const currentContext = $currentChatId ? $chatContexts.get($currentChatId) : null;

    return {
      currentChatId: $currentChatId,
      currentContextType: $currentContextType,
      isGenerating: currentContext?.isGenerating || false,
      showThinkingState: currentContext?.isGenerating || false,
      showEmptyState: !currentContext?.hasMessages && !currentContext?.isGenerating,
    } as ChatState;
  });

  // Generate a unique chat ID for a context using hash
  generateChatId(customerCode: string, contextType: "projects" | "documents", ids: ChatIds): string {
    // Create a hash from the sorted IDs to keep the key manageable
    const sortedIds = ids.slice().sort();
    const hashInput = `${customerCode}_${contextType}_${sortedIds.join("_")}`;

    // Simple hash function (you could use a more sophisticated one if needed)
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
      const char = hashInput.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    const storageKey = `chat_${Math.abs(hash).toString(36)}`;

    // Check if we already have this chat context
    if (this.chatContexts.has(storageKey)) {
      return storageKey;
    }

    // Create new chat context
    const chatContext: ChatContext = {
      chatId: storageKey,
      contextType,
      isGenerating: false,
      hasMessages: false,
    };

    this.chatContexts.set(storageKey, chatContext);
    this.chatContextsStore.set(this.chatContexts);

    return storageKey;
  }

  // Set the current chat context
  setCurrentChat(chatId: string | null, contextType: "projects" | "documents" | null = null): void {
    this.currentChatId = chatId;
    this.currentContextType = contextType;
    this.currentChatStore.set(chatId);
    this.currentContextTypeStore.set(contextType);

    // If we have a chatId, ensure context exists and update hasMessages accordingly
    if (chatId) {
      let context = this.chatContexts.get(chatId);
      if (!context) {
        // Create context if it doesn't exist (needed when using session IDs)
        context = {
          chatId: chatId,
          contextType: contextType || "documents",
          isGenerating: false,
          hasMessages: false,
        };
        this.chatContexts.set(chatId, context);
      }
      // Check if there are messages in the chat store
      const messages = getMessagesForChat(chatId);
      context.hasMessages = messages.length > 0;
      this.chatContextsStore.set(this.chatContexts);
    }
  }

  // Start a chat request for a specific chat context
  startChat(chatId: string, requestId: string): void {
    const context = this.chatContexts.get(chatId);
    if (context) {
      context.isGenerating = true;
      context.requestId = requestId;
      context.hasMessages = true; // User message was added
      this.chatContextsStore.set(this.chatContexts);
    }
  }

  // Complete a chat request for a specific chat context
  completeChat(chatId: string, requestId: string): void {
    const context = this.chatContexts.get(chatId);
    if (context && context.requestId === requestId) {
      context.isGenerating = false;
      context.requestId = undefined;
      this.chatContextsStore.set(this.chatContexts);
    }
  }

  // Check if a specific chat is generating
  isChatGenerating(chatId: string): boolean {
    const context = this.chatContexts.get(chatId);
    return context?.isGenerating || false;
  }

  // Get the current chat state
  getCurrentChatState(): ChatState | null {
    if (!this.currentChatId) return null;

    const context = this.chatContexts.get(this.currentChatId);
    if (!context) return null;

    return {
      currentChatId: this.currentChatId,
      currentContextType: this.currentContextType,
      isGenerating: context.isGenerating,
      showThinkingState: context.isGenerating,
      showEmptyState: !context.hasMessages && !context.isGenerating,
    };
  }

  // Clear all chat contexts
  clear(): void {
    this.chatContexts.clear();
    this.currentChatId = null;
    this.currentContextType = null;
    this.chatContextsStore.set(this.chatContexts);
    this.currentChatStore.set(null);
    this.currentContextTypeStore.set(null);
  }

  // Get messages for a specific chat
  getMessagesForChat(chatId: string) {
    return getMessagesForChat(chatId);
  }

  // Append assistant message to a specific chat context
  appendAssistantMessage(chatId: string, content: string, chat_qa_id?: string): void {
    // Save the message to the chat store
    appendAssistantMessage(chatId, content, chat_qa_id);

    // Update the context to reflect that it now has messages
    const context = this.chatContexts.get(chatId);
    if (context) {
      context.hasMessages = true;
      this.chatContextsStore.set(this.chatContexts);
    }
  }

  // Get all chat contexts for debugging
  getAllChatContexts(): Map<string, ChatContext> {
    return this.chatContexts;
  }
}

// Create a singleton instance
export const chatManager = new UnifiedChatManager();
